/**
 * Single source of truth for Foundations session durations.
 * Cards, course pages and JSON-LD courseWorkload must read from here.
 */
export type SessionDuration = {
  minutes: number;
  /** Long label, e.g. "1 hour per session". */
  label: string;
  /** Short label for quick-fact chips. */
  short: string;
  /** Length of one session, e.g. "1 hour". */
  length: string;
  /** ISO 8601 duration for JSON-LD courseWorkload. */
  iso: string;
};

const ONE_HOUR: SessionDuration = {
  minutes: 60,
  length: '1 hour',
  label: '1 hour per session',
  short: '1 hour / session',
  iso: 'PT1H',
};

const NINETY_MINUTES: SessionDuration = {
  minutes: 90,
  length: '1.5 hours',
  label: '1.5 hours per session',
  short: '1.5 hours / session',
  iso: 'PT1H30M',
};

/** yearLevel 0 = Kindergarten / Prep / Pre-primary. */
export const foundationsSessionDuration = (yearLevel: number): SessionDuration =>
  yearLevel >= 7 ? NINETY_MINUTES : ONE_HOUR;

/**
 * Durations for non-Foundations courses and the course catalogue cards.
 * `card` = Programs page card text, `page` = course page copy,
 * `iso` = JSON-LD courseWorkload, `schedule` = weekly CourseSchedule (optional).
 */
export type CourseDuration = {
  card: string;
  page: string;
  iso: string;
  bullet?: string;
  schedule?: { repeatFrequency: string; duration: string };
};

export const COURSE_DURATIONS = {
  foundations: {
    card: '1 hour per session (to Year 6), 1.5 hours (Years 7-8)',
    page: '1 hour per session (to Year 6), 1.5 hours per session (Years 7-8)',
    iso: ONE_HOUR.iso,
  },
  selectiveTmrw: {
    card: '4 hours per week (2 x 2 hours or 1 x 4 hours)',
    page: '4 hours per week (2 x 2-hour sessions or 1 x 4-hour block)',
    iso: 'PT4H',
    schedule: { repeatFrequency: 'P1W', duration: 'PT4H' },
  },
  vsec: {
    card: '4 hours per week (2 x 2 hours or 1 x 4 hours)',
    page: '4 hours per week (2 x 2-hour sessions or 1 x 4-hour block)',
    iso: 'PT4H',
    schedule: { repeatFrequency: 'P1W', duration: 'PT4H' },
  },
  ocTmrw: {
    card: '2.5 hours per session, once a week',
    page: '2.5 hours per session, one session per week',
    iso: 'PT2H30M',
  },
  gateTmrw: {
    card: '3 hours per session, once a week',
    page: '3 hours per session, alternating weekly with GATE PTC',
    bullet: 'Alternating weekly with GATE PTC, 3 hours each',
    iso: 'PT3H',
  },
  gatePtc: {
    card: '3 hours per session',
    page: '3 hours per session (115-minute test + 60-minute explanations)',
    iso: 'PT3H',
  },
  naplan: {
    card: '2 hours per session, once a week',
    page: '2 hours per session, alternating weekly courses',
    iso: 'PT2H',
  },
} satisfies Record<string, CourseDuration>;
